import classNames from "classnames";
import React, { ReactElement } from "react";
import sanitizeHtml from "sanitize-html";
import Styles from "./HtmlRenderer.module.scss";

export enum AttributeSize {
    SMALL = "sm",
    MEDIUM = "md",
    LARGE = "lg",
}

export enum Align {
    LEFT = "left",
    CENTER = "center",
    RIGHT = "right",
}

export const SHADOW_CLASS = {
    [AttributeSize.SMALL]: "shadow-sm",
    [AttributeSize.MEDIUM]: "shadow",
    [AttributeSize.LARGE]: "shadow-lg",
};

export const BORDER_CLASS = {
    [AttributeSize.SMALL]: "border rounded-1",
    [AttributeSize.MEDIUM]: "border rounded-3",
    [AttributeSize.LARGE]: "border rounded-4",
};

export const ALIGN_CLASS = {
    [Align.LEFT]: "",
    [Align.CENTER]: "mx-auto d-block",
    [Align.RIGHT]: "ms-auto d-block",
};

export enum StripFormatting {
    SEARCH_RESULTS = "search-results",
    LIMITTED = "limitted",
    ALL = "all"
}

export interface HtmlRendererProps {
    className?: string;
    html: string;
    stripFormatting?: "limitted" | "all" | "search-results";
    shouldTrimBottom?: boolean;
    shouldAllowMedia?: boolean;
    isArticle?: boolean;
}

const ALLOWED_TAGS_SEARCH_RESULTS = [
    "em",
];

const ALLOWED_TAGS_STRIPPED = [
    "address", "article", "dl", "dt", "p", "pre", "br", "span",
];

const ALLOWED_TAGS = [
    "address", "article", "aside", "footer", "header", "h1", "h2", "h3", "h4",
    "h5", "h6", "hgroup", "main", "nav", "section", "blockquote", "dd", "div",
    "dl", "dt", "figcaption", "figure", "hr", "li", "main", "ol", "p", "pre",
    "ul", "a", "abbr", "b", "bdi", "bdo", "br", "cite", "code", "data", "dfn",
    "em", "i", "kbd", "mark", "q", "rb", "rp", "rt", "rtc", "ruby", "s", "samp",
    "small", "span", "strong", "sub", "sup", "time", "u", "var", "wbr", "caption",
    "col", "colgroup", "table", "tbody", "td", "tfoot", "th", "thead", "tr",
    "mention", "variable",
];

function createMediaAttributes(attributes: Record<string, string>): Record<string, string> {
    const mediaClassNames = [];

    if (attributes.shadow) {
        mediaClassNames.push(SHADOW_CLASS[attributes.shadow as AttributeSize]);
    }
    if (attributes.border) {
        mediaClassNames.push(BORDER_CLASS[attributes.border as AttributeSize]);
    }
    if (attributes.align) {
        mediaClassNames.push(ALIGN_CLASS[attributes.align as Align]);
    }

    const width = typeof attributes.width === "string" ? parseInt(attributes.width, 10) : attributes.width;
    const height = typeof attributes.height === "string" ? parseInt(attributes.height, 10) : attributes.height;

    return {
        ...attributes,
        class: mediaClassNames.join(" "),
        width: width ? `${width}px` : "",
        height: height ? `${height}px` : "",
        nonce: window.NONCE,
    };
}

export function santitize(html: string, settings?: {
    stripFormatting?: StripFormatting,
    shouldAllowMedia?: boolean
}): string {
    const { stripFormatting, shouldAllowMedia } = settings || { shouldAllowMedia: false };
    let tags = ALLOWED_TAGS;
    if (stripFormatting) {
        if (stripFormatting === StripFormatting.LIMITTED) {
            tags = ALLOWED_TAGS_STRIPPED;
        } else if (stripFormatting === StripFormatting.SEARCH_RESULTS) {
            tags = ALLOWED_TAGS_SEARCH_RESULTS;
        } else if (stripFormatting === StripFormatting.ALL) {
            tags = [];
        }
    }
    if (shouldAllowMedia) {
        tags = tags.concat(["video", "img", "source"]);
    }

    const allowedAttributes: Record<string, string[]> = stripFormatting != null ? {} : {
        // eslint-disable-next-line id-length
        a: ["href", "name", "target"],
        mention: ["data-id"],
        img: ["src"],
        source: ["src", "type"],
        variable: [
            "data-field-Id", "data-group-Id", "data-replacement",
            "data-text", "data-type", "contenteditable", "title", "class",
        ],
    };

    if (shouldAllowMedia && !stripFormatting) {
        allowedAttributes.video = ["controls", "class", "width", "height"];
        allowedAttributes.img = ["src", "class", "width", "height"];
    }
    return sanitizeHtml(html, {
        allowedTags: tags,
        allowedAttributes,
        textFilter: stripFormatting === StripFormatting.ALL ? text => `${text.replace(/&nbsp;/g, " ")} ` : undefined,
        transformTags: {
            // eslint-disable-next-line id-length
            a: (__tagName, attributes) => ({
                tagName: "a",
                attribs: {
                    ...attributes,
                    target: "_blank",
                    rel: "noreferrer noopener",
                },
            }),
            video: (__tagName, attributes) => ({
                tagName: "video",
                attribs: {
                    ...createMediaAttributes(attributes),
                    controls: attributes.controls ? "controls" : "",
                },
            }),
            img: (__tagName, attributes) => ({
                tagName: "img",
                attribs: createMediaAttributes(attributes),
            }),
        },
    });
}

export function HtmlRenderer(props: HtmlRendererProps): ReactElement {
    const htmlBody = santitize(props.html, {
        stripFormatting: props.stripFormatting as StripFormatting,
        shouldAllowMedia: props.shouldAllowMedia,
    });
    return (
        <div
            className={classNames(
                props.className,
                props.shouldTrimBottom ? Styles["html-content"] : "",
                props.isArticle ? Styles.article : "",
            )}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: htmlBody.trim() }}
        />
    );
}

HtmlRenderer.defaultProps = {
    className: "",
    stripFormatting: undefined,
    shouldTrimBottom: false,
    shouldAllowMedia: false,
    isArticle: false,
};
